import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import type { Profile } from "@vimos/api-client";
import { supabase } from "./supabase";

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthState>({ session: null, profile: null, isLoading: true });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ session: null, profile: null, isLoading: true });

  useEffect(() => {
    let isMounted = true;

    async function loadProfile(session: Session | null) {
      if (!session) {
        if (isMounted) setState({ session: null, profile: null, isLoading: false });
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      if (isMounted) setState({ session, profile: profile ?? null, isLoading: false });
    }

    supabase.auth.getSession().then(({ data }) => loadProfile(data.session));

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      loadProfile(session);
    });

    return () => {
      isMounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

/** true si el usuario puede crear/editar contenido (admin o editor). */
export function canEdit(profile: Profile | null): boolean {
  return profile?.role === "admin" || profile?.role === "editor";
}
